import type { AgentContent, TextContent, ThinkingContent } from '@startech/openapi'
import type { AgentMessage } from 'modules/coaching/types'
import type { Dispatch, SetStateAction } from 'react'

export type SetMessages = Dispatch<SetStateAction<AgentMessage[]>>

let temporaryMessageIdCounter = 0

export const generateTemporaryMessageId = (): string => `msg_${Date.now()}_${++temporaryMessageIdCounter}`

export type ChatStreamSession = {
  streamingAgentMessageId?: string
  streamedContents: AgentContent[]
  thinkingMessageIds: string[]
  accumulatedThought: string
  savedAgentMessageId?: string
}

export const createChatStreamSession = (): ChatStreamSession => ({
  streamedContents: [],
  thinkingMessageIds: [],
  accumulatedThought: '',
})

export const createAgentMessage = (messageId: string, contents: AgentContent[]): AgentMessage => ({
  id: messageId,
  role: 'agent',
  contents,
  timestamp: new Date(),
})

const appendTextDelta = (contents: AgentContent[], delta: string): AgentContent[] => {
  if (!delta) {
    return contents
  }
  const nextContents = [...contents]
  const lastIndex = nextContents.length - 1
  const lastContent = lastIndex >= 0 ? nextContents[lastIndex] : undefined
  if (lastContent?.type === 'text') {
    nextContents[lastIndex] = { ...lastContent, text: lastContent.text + delta }
    return nextContents
  }
  const textContent: TextContent = { type: 'text', text: delta }
  nextContents.push(textContent)
  return nextContents
}

const applyStreamedContents = (
  session: ChatStreamSession,
  contents: AgentContent[],
  setMessages: SetMessages
): ChatStreamSession => {
  const nextSession = { ...session, streamedContents: contents }
  if (!nextSession.streamingAgentMessageId) {
    const streamingAgentMessageId = generateTemporaryMessageId()
    setMessages(previous => [...previous, createAgentMessage(streamingAgentMessageId, contents)])
    return { ...nextSession, streamingAgentMessageId }
  }
  const messageId = nextSession.streamingAgentMessageId
  setMessages(previous => previous.map(message => (message.id === messageId ? { ...message, contents } : message)))
  return nextSession
}

export const applyTextDelta = (
  session: ChatStreamSession,
  text: string | undefined,
  setMessages: SetMessages
): ChatStreamSession =>
  applyStreamedContents(session, appendTextDelta(session.streamedContents, text ?? ''), setMessages)

export const applyContentBlockAdded = (
  session: ChatStreamSession,
  content: AgentContent,
  setMessages: SetMessages
): ChatStreamSession => applyStreamedContents(session, [...session.streamedContents, content], setMessages)

const appendThinkingDelta = (contents: AgentContent[], delta: string): AgentContent[] => {
  const nextContents = [...contents]
  const lastIndex = nextContents.length - 1
  const lastContent = lastIndex >= 0 ? nextContents[lastIndex] : undefined
  if (lastContent?.type === 'thinking') {
    nextContents[lastIndex] = { ...lastContent, thought: lastContent.thought + delta }
    return nextContents
  }
  nextContents.push({ type: 'thinking', thought: delta })
  return nextContents
}

export const applyThinkingEvent = (
  session: ChatStreamSession,
  message: string | undefined,
  setMessages: SetMessages
): ChatStreamSession => {
  if (!message) {
    return session
  }
  const nextSession = { ...session, accumulatedThought: session.accumulatedThought + message }
  const existingId = nextSession.thinkingMessageIds[nextSession.thinkingMessageIds.length - 1]
  if (existingId) {
    setMessages(previous =>
      previous.map(previousMessage =>
        previousMessage.id === existingId
          ? { ...previousMessage, contents: appendThinkingDelta(previousMessage.contents, message) }
          : previousMessage
      )
    )
    return nextSession
  }
  const thinkingMessageId = generateTemporaryMessageId()
  const thinkingContent: ThinkingContent = { type: 'thinking', thought: message }
  setMessages(previous => [
    ...previous,
    {
      id: thinkingMessageId,
      role: 'agent',
      contents: [thinkingContent],
      timestamp: new Date(),
      thinkingOpen: true,
    },
  ])
  return { ...nextSession, thinkingMessageIds: [...nextSession.thinkingMessageIds, thinkingMessageId] }
}

const prependThinkingContent = (contents: AgentContent[], thought: string): AgentContent[] => {
  if (!thought || contents.some(content => content.type === 'thinking')) {
    return contents
  }
  return [{ type: 'thinking', thought }, ...contents]
}

export const applyAgentMessageSaved = (
  session: ChatStreamSession,
  messageId: string,
  contents: AgentContent[],
  setMessages: SetMessages
): ChatStreamSession => {
  const thinkingIds = new Set(session.thinkingMessageIds)
  const streamingAgentMessageId = session.streamingAgentMessageId
  const finalMessage = createAgentMessage(messageId, prependThinkingContent(contents, session.accumulatedThought))
  setMessages(previous => [
    ...previous.filter(
      message => message.id !== messageId && message.id !== streamingAgentMessageId && !thinkingIds.has(message.id)
    ),
    finalMessage,
  ])
  return {
    ...session,
    streamingAgentMessageId: undefined,
    streamedContents: [],
    thinkingMessageIds: [],
    savedAgentMessageId: messageId,
  }
}
